/**
 * Client-side face count detection via face-api.js TinyFaceDetector.
 *
 * Singleton pattern: model weights load ONCE per session via a shared
 * Promise. Subsequent detections reuse the warm runtime — first call
 * ~1-3s (model fetch + warm-up), follow-ups ~200-500ms in WASM, faster
 * if a WebGL backend is available.
 *
 * Lazy import: face-api.js is dynamically imported on first call so it
 * doesn't bloat the main bundle. Vite code-splits it into its own chunk.
 *
 * Weights live in /models — served from web/public/models/. We ship just
 * tiny_face_detector_model-shard1 + manifest (~190KB total, quantized uint8).
 */

import type * as FaceApiNS from 'face-api.js';

let faceapi: typeof FaceApiNS | null = null;
let modelsLoaded = false;
let loadingPromise: Promise<void> | null = null;

/**
 * Where face-api.js fetches weights from. Path is relative to the served
 * site root — `/models` resolves to `web/public/models/...` in Vite.
 */
const MODELS_URL = '/models';

/**
 * Hard timeout on model load — if network is dead we don't want to hang
 * forever. Above this, the caller treats it as "ML unavailable" and
 * gracefully skips face validation.
 */
const MODEL_LOAD_TIMEOUT_MS = 10_000;

async function ensureModelsLoaded(): Promise<void> {
  if (modelsLoaded) return;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    // Lazy import. Vite emits a separate chunk for this — confirmed via
    // dist/assets after `npm run build`.
    const mod = await import('face-api.js');
    faceapi = mod;

    const loadOp = mod.nets.tinyFaceDetector.loadFromUri(MODELS_URL);
    const timeoutOp = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`face-api models load timed out after ${MODEL_LOAD_TIMEOUT_MS}ms`)),
        MODEL_LOAD_TIMEOUT_MS,
      ),
    );

    await Promise.race([loadOp, timeoutOp]);
    modelsLoaded = true;
  })();

  try {
    await loadingPromise;
  } catch (e) {
    // Reset so a future call can retry (e.g. user uploads next photo
    // after their network came back).
    loadingPromise = null;
    throw e;
  }
}

export interface FaceDetectionResult {
  /** Number of faces detected in the image. */
  faceCount: number;
  /**
   * Bounding box of the first face (image-space pixels, not normalized).
   * Undefined if no face.
   */
  firstBox?: { x: number; y: number; width: number; height: number };
  /**
   * First face's bounding-box area as a percentage of total image area (0-100).
   * Used by validation to flag photos where the face is too small for the AI
   * to preserve identity well.
   *
   * Thresholds (used in PhotoUpload.tsx, not enforced here):
   *   - >= 15% : optimal
   *   - 5-15%  : usable but warn (lower-quality result)
   *   - < 5%   : reject (face too small)
   *
   * Undefined if no face.
   */
  faceAreaPercent?: number;
}

/**
 * Detect faces in `file`. Returns the count + first box.
 *
 * Throws on:
 *   - Model load failure (network dead, timeout, etc.)
 *   - Image decode failure (corrupt file, unsupported format past validation)
 *
 * Caller catches and treats throws as "ML unavailable, allow upload" per
 * the graceful-degradation policy (see PhotoUpload.validateFile).
 */
/**
 * Minimum face bounding-box size (image-space pixels). Real faces in our
 * uploads (typically 600×600+ photos) are >=100px wide. Detections smaller
 * than this are almost always noise — texture artifacts, distant background
 * objects, or duplicate detections of the same face at a smaller scale.
 *
 * Filtering by size complements the lower confidence threshold below: we
 * keep low-confidence detections (so close-together faces both register)
 * but drop the tiny phantom boxes that come with that.
 */
const MIN_FACE_BOX_PX = 80;

export async function detectFaces(file: File): Promise<FaceDetectionResult> {
  await ensureModelsLoaded();
  if (!faceapi) throw new Error('face-api not loaded');

  const img = await fileToImage(file);
  const raw = await faceapi.detectAllFaces(
    img,
    new faceapi.TinyFaceDetectorOptions({
      // 608 instead of the 416 default. 416 is the trained-scale sweet spot
      // for speed but misses small faces — a 600×600 photo with two
      // side-by-side faces (~200px each) had one face downscaled to ~80px
      // which fell below TinyFaceDetector's reliable range at 416. 608
      // preserves more detail; cost is ~2× compute (still <500ms in WASM).
      inputSize: 608,
      // 0.4 — middle ground. 0.5 (default) misses 2nd face in close-together
      // pairs; 0.3 finds phantom faces on textures/shadows. 0.4 caught both
      // real faces AND avoided false positives in our empirical testing.
      scoreThreshold: 0.4,
    }),
  );

  // Drop tiny boxes — these are noise, not faces. See MIN_FACE_BOX_PX.
  const filtered = raw.filter(
    (d) => d.box.width >= MIN_FACE_BOX_PX && d.box.height >= MIN_FACE_BOX_PX,
  );

  // Face area as % of total frame — used for "face too small" rejection.
  // Computed against the image's natural dimensions (not the 608 input
  // size we pass to the detector).
  const frameArea = img.naturalWidth * img.naturalHeight;
  const firstFace = filtered[0];
  const faceAreaPercent =
    firstFace && frameArea > 0
      ? +((firstFace.box.width * firstFace.box.height * 100) / frameArea).toFixed(2)
      : undefined;

  // eslint-disable-next-line no-console
  console.log(
    `[face-api] ${filtered.length} face(s) detected, first faceArea=${faceAreaPercent ?? '-'}% ` +
      `(raw=${raw.length}, dropped small=${raw.length - filtered.length}, ` +
      `frame=${img.naturalWidth}×${img.naturalHeight})`,
    {
      kept: filtered.map((d) => ({ score: d.score, box: d.box })),
      dropped: raw
        .filter((d) => d.box.width < MIN_FACE_BOX_PX || d.box.height < MIN_FACE_BOX_PX)
        .map((d) => ({ score: d.score, box: d.box })),
    },
  );

  return {
    faceCount: filtered.length,
    firstBox: firstFace
      ? {
          x: firstFace.box.x,
          y: firstFace.box.y,
          width: firstFace.box.width,
          height: firstFace.box.height,
        }
      : undefined,
    faceAreaPercent,
  };
}

function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };
    img.src = url;
  });
}
